import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { mqUser, mqMin } from 'utils/responsive';
import LogoImg from 'ui/images/top_for_agent/logo_navy.svg';
import { AgentSignUpRequest } from 'ui/components/company/screens/modals/AgentSignUpRequest/AgentSignUpRequest';
import SpBlueLogoImg from 'ui/images/top/sp_many_logo_blue.svg';
import { Only } from 'ui/components/elements/Only/Only';
import CloseIcon from 'ui/images/icons/close_icon.svg';
import { useModal } from 'ui/components/screens/modals/useModal';

const Header = styled.header`
    width: 100%;
    font-weight: 700;
    transition:
        transform 0.3s ease,
        opacity 0.3s ease,
        background-color 0.3s;
    opacity: 1;
    top: 0;
    left: 0;
    z-index: 1000;

    ${(props) =>
        props.isAgentTopPage
            ? `
        background-color: ${props.isscrolled ? 'rgba(255,255,255,0.85)' : 'transparent'};
        `
            : `
        background-color: var(--lightblue);
        `}
    position: ${(props) => (props.isfixed ? 'fixed' : 'static')};
    box-shadow: ${(props) => (props.isAgentTopPage ? 'none' : '0 2px 10px rgba(0, 0, 0, 0.1)')};

    &.hidden {
        transform: translateY(-100%);
        opacity: 0;
    }

    ${mqMin('NARROW')} {
        position: ${(props) => (props.isAgentTopPage ? 'absolute' : 'relative')};
        padding: 0;
        background-color: ${(props) => (props.isAgentTopPage ? 'transparent' : ' var(--lightblue)')};
        transition:
            transform 0.3s ease,
            opacity 0.3s ease,
            background-color 0.3s;
        opacity: 1;
    }
`;

const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    width: 87.67441860465116%;
    padding: 25px 0 13px 0;
    margin: 0 auto 0 auto;

    ${mqMin('SP')} {
        align-items: center;
    }

    ${mqMin('NARROW')} {
        width: 92.01388888888889%;
    }
`;

const Title = styled.h1`
    width: 6rem;
    height: auto;
    font-size: 1rem;
    margin: 0 auto;

    img {
        width: 100%;
        cursor: pointer;
    }

    ${mqMin('SP')} {
        width: 10rem;
    }

    ${mqMin('NARROW')} {
        width: 152px;
        min-width: 152px;
        height: 83px;
        margin-bottom: auto;
    }
`;

const Nav = styled.nav`
    width: calc(100% - 98px);
    max-width: 340px;
    display: flex;
    gap: 10px;
    flex-direction: column;
    flex-wrap: wrap-reverse;
    justify-content: flex-end;
    padding-bottom: 11px;

    ${mqMin('SP')} {
        max-width: initial;
    }

    ${mqMin('NARROW')} {
        width: fit-content;
    }
`;

const NavItem = styled.a`
    display: block;
    var(--font-large);
    text-align: center;
    cursor: pointer;
    padding: 6px 15px;
    font-weight: 700;
     white-space: nowrap;

    &:visited {
        color: var(--white);
    }

      &:hover {
        transform: scale(1.05);
        transition: transform 0.3s ease;
    }
`;

const LoginLink = styled(NavItem)`
    border: solid 1px var(--dark-blue);
    border-radius: 7px;
    line-height: 1em;
    transition: all 0.3s;
    color: var(--dark-blue);
    background-color: var(--white);

    &:hover {
        color: var(--white);
        background-color: var(--dark-blue);
    }
`;

const RegisterLink = styled(NavItem)`
    background-color: var(--dark-blue);
    border: solid 1px #fff;
    border-radius: 7px;
    line-height: 1em;
    transition: all 0.3s;
    color: var(--white);
`;

const RegisterLinkForOther = styled(NavItem)`
    font-size: var(--font-default);
    margin-bottom: 18px;
    transition: all 0.3s;
    border: 1px solid var(--light-orange);
    box-shadow: 0px 4px 4px 0px #00000040;
    cursor: pointer;
    padding: 10px 15px;
    font-weight: 700;
    width: fit-content;
    border-radius: 7px;
    color: var(--primary);
    background-color: var(--white);

    ${mqMin('SP')} {
        margin: auto 0;
    }

    &:hover {
        color: var(--white);
        background-color: var(--light-orange);
        transform: scale(1.05);
        transition: transform 0.3s ease;
    }
`;

const Spacer = styled.div`
    display: none;
    ${mqUser('SP')} {
        display: block;
        height: 121px;
        background-color: var(--pale-blue);
    }
`;

const CenterNav = styled.div`
    display: flex;
    justify-content: end;
    gap: 30px;
    color: var(--dark-blue);
    font-weight: 700;
    font-size: var(--font-large);
    width: 100%;
    white-space: nowrap;
    margin: 18px 40px auto auto;

    span {
        display: inline-block;
        vertical-align: top;
        cursor: pointer;
        &:hover {
            transform: scale(1.05);
            transition: transform 0.3s ease;
        }
    }
`;

const ForAgentButtons = styled.div`
    display: flex;
    border-radius: 8px;
    background-color: ${(props) => (props.isAgentTopPage ? ' var(--lightblue)' : ' var(--white)')};
    color: var(--dark-blue);
    padding: 12px 16px;
    align-items: center;
    width: fit-content;
    gap: 14px;

    span {
        font-size: 11px;
        white-space: nowrap;
        cursor: pointer;
    }
`;

const LinkContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const AgentLoginButton = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 7px;
    border: 1px solid var(--primary);
    font-weight: 700;
    cursor: pointer;
    text-align: center;
    width: fit-content;

    span {
        background-color: var(--primary);
        white-space: nowrap;
        color: var(--white);
        padding: 0.4rem;
        width: 100%;
        border-radius: 7px 7px 0 0;
        font-size: 0.8rem;
    }

    div {
        background-color: var(--white);
        white-space: nowrap;
        color: var(--primary);
        padding: 0.5rem 0.6rem;
        border-radius: 0 0 7px 7px;
        font-size: 0.9rem;
    }
`;

const HamburgerButton = styled.button`
    background: none;
    border: none;
    padding: 0 16px 0 0;
    display: ${(props) => (props.menuOpen ? 'none' : 'flex')};
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 2.7rem;
    cursor: pointer;
    z-index: 1201;

    span {
        display: block;
        width: 2.7rem;
        height: 4px;
        margin: 8px 0;
        background: var(--dark-blue);
        border-radius: 2px;
        transition: 0.3s;
    }

    ${mqMin('SP')} {
        width: 5rem;

        span {
            width: 5rem;
        }
    }
`;

// スライドインメニュー
const MobileMenuDrawer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1200;
    display: flex;
    pointer-events: none;

    .drawer-content {
        width: 100vw;
        background: var(--lightblue);
        height: 100vh;
        overflow-y: auto;
        transform: translateX(-100%);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: auto;
        display: flex;
        flex-direction: column;
        position: relative;
    }
    &.open .drawer-content {
        transform: translateX(0);
    }
    &.open {
        pointer-events: auto;
    }
`;

const CloseButton = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 4.3rem;
    height: 4.3rem;
    cursor: pointer;
    z-index: 1300;
    background-color: var(--white);
    border-radius: 0 0 0 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
        width: 2rem;
        height: auto;
    }

    ${mqMin('SP')} {
        width: 6rem;
        height: 6rem;
        img {
            width: 3rem;
        }
    }
`;

const SpBlueLogo = styled.div`
    width: 6rem;
    height: auto;
    margin: 1rem auto;
    img {
        width: 100%;
    }

    ${mqMin('SP')} {
        width: 10rem;
    }
`;

const MenuContainer = styled.div`
    width: 20rem;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin: 2rem auto;

    ${mqMin('SP')} {
        width: 40rem;
    }
`;

const ForAgent = styled.div`
    width: 20rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 1rem auto 3rem auto;

    ${mqMin('SP')} {
        width: 40rem;
    }
`;

const MenuItemForAgent = styled.div`
    width: 100%;
    background-color: var(--white);
    border-radius: 8px;
    border: 1px solid var(--dark-blue);
    padding: 1rem;
    color: var(--dark-blue);
    font-weight: 700;
    cursor: pointer;
    font-size: 1rem;

    ${mqMin('SP')} {
        padding: 1.5rem;
        font-size: 1.4rem;
    }
`;

const MenuItem = styled.div`
    width: 100%;
    background-color: var(--white);
    border-radius: 8px;
    padding: 1rem;
    color: var(--dark-blue);
    font-weight: 700;
    cursor: pointer;
    font-size: 1rem;

    ${mqMin('SP')} {
        padding: 1.5rem;
        font-size: 1.4rem;
    }
`;

const SellerButtonContainer = styled.div`
    width: 20rem;
    display: flex;
    flex-direction: column;
    gap: 0.7rem;
    background-color: var(--white);
    border-radius: 8px;
    padding: 1.6rem;
    color: var(--dark-blue);
    font-weight: 700;
    font-size: 1rem;
    margin: 0 auto;
    text-align: center;

    ${mqMin('SP')} {
        width: 40rem;
        font-size: 1.4rem;
    }
`;

const LoginButton = styled.button`
    background-color: var(--white);
    border: 1px solid var(--blue);
    border-radius: 8px;
    color: var(--blue);
    height: 3rem;
    font-weight: 700;
    cursor: pointer;
    font-size: 1rem;
    width: 17rem;
    margin: 1.2rem auto 0 auto;

    ${mqMin('SP')} {
        font-size: 1.4rem;
    }
`;

const SignupButton = styled.button`
    background-color: var(--orange);
    border: 1px solid var(--orange);
    border-radius: 8px;
    color: var(--white);
    height: 3rem;
    font-weight: 700;
    cursor: pointer;
    font-size: 1rem;
    width: 17rem;
    margin: 0 auto;

    ${mqMin('SP')} {
        font-size: 1.4rem;
    }
`;

const SpFooter = styled.div`
    width: 100%;
    height: 6rem;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--dark-blue);
    color: var(--white);
    gap: 1rem;
    font-size: 0.9rem;
    padding: 2rem 0.8rem;
    bottom: 0;
    position: relative;
    margin-top: auto;

    ${mqMin('SP')} {
        font-size: 1.2rem;
    }
`;

const SpMenuHeader = styled.div`
    position: sticky;
    top: 0;
    left: 0;
    width: 100%;
    background-color: var(--lightblue);
    z-index: 10;
    flex-shrink: 0;
`;

const OtherLinkContainer = styled.div`
    display: flex;
    gap: 0.6rem;
    justify-content: center;
    align-items: center;
`;

export const TopHeaderForAgent = () => {
    const navigate = useNavigate();
    const [isFixed, setIsFixed] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const preOpenModal = useModal();
    const { openModal, createModal } = preOpenModal;

    const isAgentTopPage = window.location.pathname === '/agent/' || window.location.pathname === '/agent';

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            setIsFixed(scrollY > 0);
            setIsScrolled(scrollY > 0);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const goToRegister = () => {
        openModal();
        setMenuOpen(false);
    };
    const goToAgentLogin = () => {
        navigate('/agent/login/');
        setMenuOpen(false);
    };
    const goToAgentTop = () => {
        navigate('/agent/');
        setMenuOpen(false);
    };
    const goToBuyerTop = () => {
        navigate('/buyer/');
    };
    const goToTop = () => {
        navigate('/');
    };
    const scrollToAboutMany = () => {
        navigate('/agent/#about-many');
        setMenuOpen(false);
    };
    const scrollToAboutService = () => {
        navigate('/agent/#about-service');
        setMenuOpen(false);
    };
    const goToContact = () => {
        window.open('/contact/', '_blank');
        setMenuOpen(false);
    };
    const scrollToNews = () => {
        navigate('/agent/#news');
        setMenuOpen(false);
    };
    const goToQA = () => {
        navigate('/agent/#qa');
        setMenuOpen(false);
    };
    const goToTerms = () => {
        window.open('/terms/', '_blank');
        setMenuOpen(false);
    };
    const goToPrivacyPolicy = () => {
        window.open('/privacy/', '_blank');
        setMenuOpen(false);
    };
    const goToCompanyProfile = () => {
        window.open('/company_profile/', '_blank');
        setMenuOpen(false);
    };
    return (
        <>
            <Header className="kv-section" isfixed={isFixed} isscrolled={isScrolled} isAgentTopPage={isAgentTopPage}>
                {createModal(<AgentSignUpRequest />)}
                <Only pc>
                    <HeaderContent>
                        <Title onClick={goToAgentTop}>
                            <img src={LogoImg} alt="MAny" />
                        </Title>
                        <CenterNav>
                            <span onClick={scrollToAboutMany}>MAnyとは</span>
                            <span onClick={scrollToAboutService}>サービスの流れ</span>
                            <span onClick={goToContact}>お問い合わせ</span>
                        </CenterNav>
                        <Nav>
                            <ForAgentButtons isAgentTopPage={isAgentTopPage}>
                                <span>M&A専門家の方</span>
                                <LinkContainer>
                                    <LoginLink onClick={goToAgentLogin}>ログイン</LoginLink>
                                    <RegisterLink onClick={goToRegister}>新規登録</RegisterLink>
                                </LinkContainer>
                            </ForAgentButtons>
                            <OtherLinkContainer>
                                <RegisterLinkForOther onClick={goToBuyerTop}>
                                    買い手企業の方はこちら
                                </RegisterLinkForOther>
                                <RegisterLinkForOther onClick={goToTop}>売り手企業の方はこちら</RegisterLinkForOther>
                            </OtherLinkContainer>
                        </Nav>
                    </HeaderContent>
                </Only>
                <Only sp>
                    <HeaderContent>
                        <HamburgerButton
                            menuOpen={menuOpen}
                            onClick={() => setMenuOpen(true)}
                            aria-label="メニューを開く"
                        >
                            <span />
                            <span />
                        </HamburgerButton>
                        <Title onClick={goToTop}>
                            <img src={LogoImg} alt="MAny" />
                        </Title>
                        <AgentLoginButton onClick={goToAgentLogin}>
                            <span>仲介会社様</span>
                            <div>ログイン</div>
                        </AgentLoginButton>
                    </HeaderContent>
                </Only>
            </Header>
            {/* スライドインメニュー */}
            <MobileMenuDrawer className={menuOpen ? 'open' : ''}>
                <div className="drawer-content">
                    <SpMenuHeader>
                        <SpBlueLogo>
                            <img src={SpBlueLogoImg} alt="MAny" onClick={goToAgentTop} />
                        </SpBlueLogo>
                        <CloseButton onClick={() => setMenuOpen(false)}>
                            <img src={CloseIcon} alt="メニューを閉じる" />
                        </CloseButton>
                    </SpMenuHeader>
                    <MenuContainer>
                        <MenuItem onClick={scrollToAboutMany}>MAnyとは</MenuItem>
                        <MenuItem onClick={scrollToNews}>最新のお知らせ</MenuItem>
                        <MenuItem onClick={scrollToAboutService}>サービスの流れ</MenuItem>
                        <MenuItem onClick={goToQA}>よくある質問</MenuItem>
                        <MenuItem onClick={goToContact}>お問い合わせ</MenuItem>
                    </MenuContainer>
                    <SellerButtonContainer>
                        M&A専門家の方
                        <LoginButton onClick={goToAgentLogin}>ログイン</LoginButton>
                        <SignupButton onClick={goToRegister}>新規登録</SignupButton>
                    </SellerButtonContainer>
                    <ForAgent>
                        <MenuItemForAgent onClick={goToTop}>売り手企業の方はこちら</MenuItemForAgent>
                        <MenuItemForAgent onClick={goToBuyerTop}>買い手企業の方はこちら</MenuItemForAgent>
                    </ForAgent>
                    <SpFooter>
                        <span onClick={goToTerms}>利用規約</span>
                        <span onClick={goToPrivacyPolicy}>プライバシーポリシー</span>
                        <span onClick={goToCompanyProfile}>運営会社</span>
                    </SpFooter>
                </div>
            </MobileMenuDrawer>
            {isFixed && (
                <Only sp>
                    <Spacer className="sp-only" style={{ height: '121px' }} />
                </Only>
            )}
        </>
    );
};
